import type { ButtonProps } from '@workramp/ui';
import type { Ref } from 'react';
import type { HubGeneral } from '../../../constants';
import { forwardRef } from 'react';
import { useHubTheming } from '../../../providers/useHubTheming';
import { getMutedColor } from '../../utils';
import { StyledButton } from './ThemeButton.styles';

type BaseThemeButtonProps = ButtonProps & {
  themeColor: string;
  themeHoverColor?: string;
  themeTextColor?: string;
  themeFontSize?: number;
  themeFontFamily?: string;
  children?: React.ReactNode;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
};

export type ThemeButtonProps = ButtonProps & {
  themeColor?: string;
  themeHoverColor?: string;
  themeTextColor?: string;
  themeFontSize?: number;
  themeBorderRadius?: number;
};

const HUB_GENERAL = 'hub.general';

export const ThemeButton = forwardRef(
  (
    {
      themeColor,
      themeHoverColor,
      themeFontSize,
      themeTextColor,
      children,
      color = 'primary',
      variant = 'solid',
      ...props
    }: ThemeButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const { renderedThemeSettings } = useHubTheming();
    const hubGeneralSettings: HubGeneral = renderedThemeSettings.find((ts) => ts.category === HUB_GENERAL).settings;
    const { colors: defaultHubColors, typography: defaultHubTypography } = hubGeneralSettings;

    const HUB_COLORS = {
      text: defaultHubColors.text,
      buttonAndLinks: defaultHubColors.buttonLinks,
      buttonText: '#FFFFFF',
    };

    const hubDefaultFontFamily = defaultHubTypography.body;
    const hubDefaultFontSize = variant === 'solid' ? defaultHubTypography.buttonSize : defaultHubTypography.bodySize;

    const hubDefaultColors = {
      primary: {
        color: HUB_COLORS.buttonAndLinks,
        textColor: HUB_COLORS.buttonText,
      },
      secondary: {
        color: getMutedColor(HUB_COLORS.text),
        hoverColor: HUB_COLORS.buttonAndLinks,
      },
    }[color];

    return (
      <BaseThemeButton
        ref={ref}
        {...props}
        variant={variant}
        themeColor={themeColor || hubDefaultColors.color}
        themeHoverColor={themeHoverColor || hubDefaultColors.hoverColor}
        themeTextColor={themeTextColor || hubDefaultColors.textColor}
        themeFontFamily={hubDefaultFontFamily}
        themeFontSize={themeFontSize || hubDefaultFontSize}
      >
        {children}
      </BaseThemeButton>
    );
  },
);

export const BaseThemeButton = forwardRef(
  (
    {
      themeColor,
      themeHoverColor,
      themeFontSize,
      themeTextColor,
      themeFontFamily,
      children,
      color,
      variant,
      ...props
    }: BaseThemeButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => (
    <StyledButton
      ref={ref}
      {...props}
      variant={variant}
      $color={themeColor}
      $hoverColor={themeHoverColor}
      $textColor={themeTextColor}
      $fontFamily={themeFontFamily}
      $fontSize={themeFontSize}
    >
      {children}
    </StyledButton>
  ),
);
